/**
 * Phase 14 Plan 14-02 — TypeScript wire types for the Logsheet Approver feature.
 *
 * Mirrors the 14-01 backend DTO contract (camelCase per Phase 1/2 JsonNamingPolicy).
 * RejectionReason is a string union matching JsonStringEnumConverter PascalCase output.
 */

import type { LogsheetStatus } from '@/types/logsheets-maker';
import type { PaginatedResult } from '@/types/roles';

/** Re-export for convenience */
export type { PaginatedResult };

/**
 * Rejection reason codes — mirrors `RejectionReasonDto` enum (Plan 13-01, reused by 14-01).
 * Backend emits PascalCase strings via JsonStringEnumConverter.
 */
export type RejectionReason =
  | 'IncorrectKpi'
  | 'MissingDoc'
  | 'WrongMachine'
  | 'DateMismatch'
  | 'Other';

/** Human-readable labels for each rejection reason. */
export const REJECTION_REASON_LABELS: Readonly<Record<RejectionReason, string>> = {
  IncorrectKpi: 'Incorrect KPI values',
  MissingDoc: 'Missing document',
  WrongMachine: 'Wrong machine',
  DateMismatch: 'Date mismatch',
  Other: 'Other',
};

/**
 * Route target for approver rejection — mirrors `ApproverRejectionRouteTargetDto` enum.
 * Maker = back to Maker (Status → Rejected); Checker = back to Checker (Status → Submitted).
 */
export type ApproverRejectionRouteTarget = 'Maker' | 'Checker';

/** A row in `GET /api/logsheets/approver/pending` (paginated list). */
export interface ApproverPendingItem {
  readonly id: number;
  readonly entryDate: string;
  readonly siteId: number;
  readonly siteName: string;
  readonly machineId: number;
  readonly machineCode: string;
  readonly machineName: string;
  /**
   * Denormalized "Vehicle Number" custom field (key `vehicle_number`), surfaced next to the
   * machine name via {@link formatMachineName}. `null` when the machine has no vehicle number
   * recorded — the machine code is the display fallback. Inherited by ApproverDetail +
   * ApproverHistoryItem.
   */
  readonly vehicleNumber: string | null;
  readonly makerUserId: number;
  readonly makerName: string;
  readonly checkedByUserId: number | null;
  readonly checkerName: string | null;
  readonly type: 'Work' | 'Idle';
  readonly kpisCount: number;
  readonly checkedAt: string | null;
  readonly daysPending: number;
  /**
   * Phase 15 Plan 15-02 (D-138): True when the (machine, month) tuple for this entry has been
   * locked by a monthly-document upload. Optional because the backend list projection
   * (ApproverPendingItemDto) does not yet include this field — the backend 15-01 plan only added
   * mutation guards, not list projection extensions. When the field is absent (undefined), the
   * badge is not shown.
   * Future backend addendum: add `MonthLocked bool` to `ApproverPendingItemDto` and service projection.
   */
  readonly monthLocked?: boolean;
}

/** Document attached to a logsheet entry (shared with Phase 12). */
export interface DocumentDto {
  readonly id: number;
  readonly fileName: string;
  readonly contentType: string;
  readonly sizeBytes: number;
  readonly uploadedAt: string;
  readonly uploadedByUserId: number;
}

/** Checker-context block returned inside the approver detail response (D-125). */
export interface CheckerInfo {
  readonly checkerUserId: number;
  readonly checkerName: string;
  readonly checkedAt: string;
  readonly checkerRemarks: string | null;
}

/** A row in the entry-history timeline (prior Submitted→Checked→Rejected transitions). */
export interface EntryHistoryItem {
  readonly actor: string;
  readonly action: string;
  readonly at: string;
  readonly reason: string | null;
  readonly remarks: string | null;
}

/** Full detail from `GET /api/logsheets/approver/pending/:id`. */
export interface ApproverDetail extends ApproverPendingItem {
  readonly machineTypeId: number;
  readonly machineTypeName: string;
  readonly kpiValuesJson: string;
  readonly documents: readonly DocumentDto[];
  readonly status: 'Submitted' | 'Checked' | 'Approved' | 'Rejected';
  readonly checkerInfo: CheckerInfo | null;
  readonly history: readonly EntryHistoryItem[];
  readonly notes: string | null;
  readonly workDescription: string | null;
  readonly idleReason: string | null;
  readonly startTime: string;
  readonly endTime: string | null;
  readonly shift: string | null;
  readonly submittedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Last-5 comparison entry from `GET /api/logsheets/approver/pending/:id/recent-entries`. */
export interface ApproverRecentEntry {
  readonly id: number;
  readonly entryDate: string;
  readonly status: string;
  readonly kpiValuesJson: string;
  readonly approvedByUserId: number | null;
  readonly approvedAt: string | null;
}

/** Request body for `POST /api/logsheets/approver/pending/:id/approve`. */
export interface ApproverApproveRequest {
  readonly remarks?: string;
}

/** Request body for `POST /api/logsheets/approver/pending/:id/reject`. */
export interface ApproverRejectRequest {
  readonly reason: RejectionReason;
  readonly remarks: string;
  readonly routeTo: ApproverRejectionRouteTarget;
}

/** A row in `GET /api/logsheets/approver/history`. */
export interface ApproverHistoryItem extends ApproverPendingItem {
  readonly status: 'Approved' | 'Rejected' | 'Submitted';
  readonly actedAt: string | null;
  readonly approverRejectionRoutedToChecker: boolean;
}

/** Request body for `POST /api/logsheets/approver/pending/bulk-approve`. */
export interface ApproverBulkApproveRequest {
  readonly ids: readonly number[];
  readonly remarks?: string;
}

/** Per-entry result row in bulk approve response. */
export interface ApproverBulkApproveItemResult {
  readonly id: number;
  readonly success: boolean;
  readonly error: string | null;
}

/** Aggregate result for bulk approve. */
export interface ApproverBulkApproveResult {
  readonly results: readonly ApproverBulkApproveItemResult[];
  readonly succeededCount: number;
  readonly failedCount: number;
}

/** Query parameters for `GET /api/logsheets/approver/pending`. */
export interface ApproverPendingListQuery {
  readonly siteId?: number;
  readonly machineId?: number;
  readonly checkerId?: number;
  readonly fromDate?: string;
  readonly toDate?: string;
  readonly page?: number;
  readonly pageSize?: number;
  /** Phase 26 — when set, overrides the default `Checked` queue filter so the
   *  approver can flip to Approved / Rejected entries from the same screen. */
  readonly status?: LogsheetStatus;
}

/** Query parameters for `GET /api/logsheets/approver/history`. */
export interface ApproverHistoryListQuery {
  readonly status?: 'Approved' | 'Rejected';
  readonly siteId?: number;
  readonly fromDate?: string;
  readonly toDate?: string;
  readonly page?: number;
  readonly pageSize?: number;
}
